/**
 * src/SecurityAdminOps.js
 *
 * Owner / authorised-admin Security Operations console for the VISINVOKE
 * Security product line (Managed PC Protection via N-able N-sight, Track, Lock &
 * Wipe via DriveStrike). Mounted behind StaffAdminRouteGuard at
 * /admin/security-operations.
 *
 * This is an INTERNAL owner surface, not a customer surface, so it deliberately
 * shows provider names, the admin exception queue and provider object ids (site,
 * client, device) that the customer surface never sees. It consumes only the
 * authorised admin API at /v1/admin/security. It performs no provider write on
 * its own: it records the owner's console actions (verify a completed removal,
 * confirm an install/enrolment, record provider ids, request a read-only
 * reconcile, preview a provision) and never executes a live provider create,
 * install, uninstall or Locate/Lock/Wipe.
 *
 * No em dashes in this file.
 */
import React, { useEffect, useState, useCallback } from 'react';
import API from './api';

const ADMIN = '/v1/admin/security';

// Task types this console knows how to act on. Anything else still lists with a
// generic Resolve action.
const TASK_LABELS = {
  nable_installer_required: 'N-able installer required',
  drivestrike_enrolment: 'DriveStrike enrolment required',
  authorised_removal: 'Authorised removal (Managed)',
  track_deprovision: 'Deprovision (Track, Lock & Wipe)',
  nable_identity_ambiguous: 'N-able device identity ambiguous',
  nable_provider_error: 'N-able provider error',
  provisioning_admin_action: 'Provisioning action required',
  stripe_quantity_mismatch: 'Stripe quantity mismatch',
  stripe_quantity_update_failed: 'Stripe quantity update failed',
};

const REMOVAL_TASKS = ['authorised_removal', 'track_deprovision'];
const ONBOARDING_TASKS = ['nable_installer_required', 'drivestrike_enrolment'];

function label(t) { return TASK_LABELS[t] || t; }

export default function SecurityAdminOps() {
  const [status, setStatus] = useState('open');
  const [typeFilter, setTypeFilter] = useState('');
  const [tasks, setTasks] = useState([]);
  const [reqs, setReqs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [mapFor, setMapFor] = useState(null);      // task id for the mapping form
  const [previewFor, setPreviewFor] = useState(null);
  const [preview, setPreview] = useState(null);

  const loadTasks = useCallback(async () => {
    setError(null);
    try {
      const r = await API.get(ADMIN + '/tasks', { params: { status: status === 'all' ? '' : status } });
      setTasks((r.data && r.data.tasks) || []);
    } catch (e) {
      setError('Could not load the Security operations queue.');
    }
  }, [status]);

  const loadReqs = useCallback(async () => {
    try {
      const r = await API.get(ADMIN + '/owner-requirements');
      setReqs(r.data || null);
    } catch (e) { /* requirements are best-effort */ }
  }, []);

  useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true);
      await Promise.all([loadTasks(), loadReqs()]);
      if (live) setLoading(false);
    })();
    return () => { live = false; };
  }, [loadTasks, loadReqs]);

  function flash(msg) {
    setToast(msg);
    window.setTimeout(() => setToast(null), 3500);
  }

  async function act(taskId, fn, okMsg) {
    setBusyId(taskId);
    try {
      await fn();
      await loadTasks();
      if (okMsg) flash(okMsg);
    } catch (e) {
      const m = (e.response && e.response.data && e.response.data.error) || 'Action failed.';
      flash(m);
    } finally {
      setBusyId(null);
      setMapFor(null);
    }
  }

  function resolveTask(t) {
    return act(t.id, () => API.post(ADMIN + '/tasks/' + t.id + '/resolve', { note: 'Resolved from Security Operations.' }),
      'Task resolved.');
  }

  function verifyRemoval(t) {
    const path = ADMIN + '/accounts/' + t.user_id + '/endpoints/' + t.endpoint_id + '/services/' + t.service + '/verify-removal';
    return act(t.id, () => API.post(path, {}),
      'Removal verified. The billed quantity will drop on the next sync.');
  }

  function markActive(t) {
    const path = ADMIN + '/accounts/' + t.user_id + '/endpoints/' + t.endpoint_id + '/services/' + t.service + '/activate';
    return act(t.id, () => API.post(path, {}), 'Marked active. Onboarding task closed.');
  }

  function reconcileAccount(t) {
    return act(t.id, () => API.post(ADMIN + '/accounts/' + t.user_id + '/refresh', {}),
      'Reconciliation requested (read-only).');
  }

  const filtered = tasks.filter((t) => !typeFilter || t.task_type === typeFilter);
  const typesPresent = Array.from(new Set(tasks.map((t) => t.task_type)));

  if (loading) {
    return <div style={s.page}><div style={s.center}>Loading Security Operations...</div></div>;
  }

  return (
    <div style={s.page}>
      <div style={s.container}>
        <div style={s.headerRow}>
          <h1 style={s.h1}>Security Operations</h1>
          <span style={s.badge}>Owner console</span>
        </div>
        <p style={s.sub}>
          The authorised worklist for provider-side actions (N-able and DriveStrike) and the Security
          exception queue. Actions here record owner console work and read-only reconciliation. No live
          provider create, install, uninstall or Locate, Lock or Wipe is performed from this page.
        </p>

        <OwnerRequirements reqs={reqs} />

        {error && <div style={s.err}>{error}</div>}

        <div style={s.filters}>
          <div style={s.filterGroup}>
            <span style={s.filterLabel}>Status</span>
            {['open', 'resolved', 'all'].map((v) => (
              <button key={v} onClick={() => setStatus(v)}
                style={{ ...s.pill, ...(status === v ? s.pillOn : {}) }}>{v}</button>
            ))}
          </div>
          <div style={s.filterGroup}>
            <span style={s.filterLabel}>Type</span>
            <button onClick={() => setTypeFilter('')} style={{ ...s.pill, ...(typeFilter === '' ? s.pillOn : {}) }}>all</button>
            {typesPresent.map((tp) => (
              <button key={tp} onClick={() => setTypeFilter(tp)}
                style={{ ...s.pill, ...(typeFilter === tp ? s.pillOn : {}) }}>{label(tp)}</button>
            ))}
          </div>
          <button onClick={loadTasks} style={s.refreshBtn}>Refresh list</button>
        </div>

        {filtered.length === 0 ? (
          <div style={s.empty}>No {status === 'all' ? '' : status} tasks{typeFilter ? ' of this type' : ''}.</div>
        ) : (
          <div style={s.taskList}>
            {filtered.map((t) => (
              <TaskCard
                key={t.id}
                t={t}
                busy={busyId === t.id}
                onResolve={() => resolveTask(t)}
                onVerifyRemoval={() => verifyRemoval(t)}
                onMarkActive={() => markActive(t)}
                onReconcile={() => reconcileAccount(t)}
                onOpenMapping={() => setMapFor(t.id === mapFor ? null : t.id)}
                mappingOpen={mapFor === t.id}
                onSubmitMapping={(payload) => act(t.id, () =>
                  API.post(ADMIN + '/accounts/' + t.user_id + '/endpoints/' + t.endpoint_id +
                    '/services/' + t.service + '/provider-mapping', payload),
                  'Provider mapping recorded.')}
                onPreview={async () => {
                  setPreviewFor(t.id); setPreview(null);
                  try {
                    const r = await API.post(ADMIN + '/accounts/' + t.user_id + '/endpoints/' + t.endpoint_id +
                      '/provision-preview', {});
                    setPreview(r.data);
                  } catch (e) { setPreview({ error: 'Preview failed.' }); }
                }}
              />
            ))}
          </div>
        )}
      </div>

      {previewFor && (
        <div style={s.overlay} onClick={() => { setPreviewFor(null); setPreview(null); }}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={s.modalTitle}>Provision preview (not executed)</h3>
            <p style={s.modalNote}>
              This is exactly what would be sent to N-able to create the client and site. Nothing has been
              created. Creating it is a separate, explicitly confirmed owner action.
            </p>
            <pre style={s.pre}>{preview ? JSON.stringify(preview, null, 2) : 'Loading...'}</pre>
            <div style={s.modalActions}>
              <button style={s.ghost} onClick={() => { setPreviewFor(null); setPreview(null); }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {toast && <div style={s.toast}>{toast}</div>}
    </div>
  );
}

function OwnerRequirements({ reqs }) {
  const [open, setOpen] = useState(false);
  if (!reqs) return null;
  const n = reqs.nable || {};
  const d = reqs.drivestrike || {};
  return (
    <div style={s.reqCard}>
      <div style={s.reqHead} onClick={() => setOpen(!open)}>
        <span style={s.reqTitle}>Provider setup and owner requirements</span>
        <span style={s.reqToggle}>{open ? 'Hide' : 'Show'}</span>
      </div>
      <div style={s.reqStatusRow}>
        <StatusDot on={!!n.configured} label={'N-able ' + (n.configured ? 'configured' : 'not configured')} />
        <StatusDot on={!!d.configured} label={'DriveStrike ' + (d.configured ? 'configured' : 'not configured')} />
        <StatusDot on={!!d.enrolment_token_configured} label={'DriveStrike enrolment token ' + (d.enrolment_token_configured ? 'present' : 'absent')} />
      </div>
      {open && (
        <div style={s.reqBody}>
          <ReqBlock title="N-able N-sight (Managed PC Protection)" model={n.verified_model} needs={n.needs} />
          <ReqBlock title="DriveStrike (Track, Lock & Wipe)" model={d.verified_model} needs={d.needs} />
          <p style={s.reqFoot}>
            Provider credentials and tokens are configured server-side in the environment only. They are
            never shown here and never reach any customer surface.
          </p>
        </div>
      )}
    </div>
  );
}

function ReqBlock({ title, model, needs }) {
  return (
    <div style={s.reqBlock}>
      <div style={s.reqBlockTitle}>{title}</div>
      {model && <div style={s.reqModel}>{model}</div>}
      {Array.isArray(needs) && needs.length > 0 && (
        <ul style={s.reqNeeds}>
          {needs.map((it, i) => <li key={i} style={s.reqNeed}>{it}</li>)}
        </ul>
      )}
    </div>
  );
}

function StatusDot({ on, label }) {
  return (
    <span style={s.statusItem}>
      <span style={{ ...s.dot, background: on ? '#34d399' : '#6b7280' }} />
      <span style={s.statusText}>{label}</span>
    </span>
  );
}

function TaskCard({ t, busy, onResolve, onVerifyRemoval, onMarkActive, onReconcile,
                    onOpenMapping, mappingOpen, onSubmitMapping, onPreview }) {
  const isRemoval = REMOVAL_TASKS.indexOf(t.task_type) !== -1;
  const isOnboarding = ONBOARDING_TASKS.indexOf(t.task_type) !== -1;
  const isNable = t.provider === 'nable';
  const isResolved = t.status !== 'open';

  return (
    <div style={s.taskCard}>
      <div style={s.taskTop}>
        <div>
          <span style={s.taskType}>{label(t.task_type)}</span>
          {t.provider && <span style={s.provTag}>{t.provider}</span>}
          {t.service && <span style={s.svcTag}>{t.service}</span>}
        </div>
        <span style={{ ...s.statusTag, color: isResolved ? '#9ca3af' : '#fbbf24' }}>{t.status}</span>
      </div>

      <div style={s.taskMeta}>
        <span>account: <code style={s.code}>{t.user_id}</code></span>
        {t.endpoint_id && <span>endpoint: <code style={s.code}>{t.endpoint_id}</code></span>}
      </div>
      {t.detail && <div style={s.taskDetail}>{t.detail}</div>}
      <div style={s.taskDate}>{t.created_at}</div>

      {!isResolved && (
        <div style={s.taskActions}>
          {isRemoval && (
            <button style={s.primary} disabled={busy} onClick={onVerifyRemoval}>
              Verify removal complete
            </button>
          )}
          {isOnboarding && (
            <button style={s.primary} disabled={busy} onClick={onMarkActive}>
              Confirm installed / enrolled
            </button>
          )}
          {isNable && (
            <button style={s.ghost} disabled={busy} onClick={onReconcile}>Reconcile (read-only)</button>
          )}
          {isNable && (
            <button style={s.ghost} disabled={busy} onClick={onOpenMapping}>
              {mappingOpen ? 'Close mapping' : 'Record N-able ids'}
            </button>
          )}
          {isNable && (
            <button style={s.ghost} disabled={busy} onClick={onPreview}>Provision preview</button>
          )}
          <button style={s.ghostQuiet} disabled={busy} onClick={onResolve}>Resolve</button>
        </div>
      )}

      {mappingOpen && <MappingForm onSubmit={onSubmitMapping} busy={busy} />}
    </div>
  );
}

function MappingForm({ onSubmit, busy }) {
  const [clientId, setClientId] = useState('');
  const [siteId, setSiteId] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [hostname, setHostname] = useState('');
  function submit() {
    const payload = {};
    if (clientId.trim()) payload.provider_client_id = clientId.trim();
    if (siteId.trim()) payload.provider_site_id = siteId.trim();
    if (deviceId.trim()) payload.provider_device_id = deviceId.trim();
    if (hostname.trim()) payload.hostname = hostname.trim();
    onSubmit(payload);
  }
  return (
    <div style={s.mapForm}>
      <div style={s.mapNote}>
        Record the N-able client, site and device ids after locating them in the N-sight dashboard. These
        internal ids let reconciliation match the installed agent. They are never shown to the customer.
      </div>
      <div style={s.mapGrid}>
        <input style={s.input} placeholder="client id" value={clientId} onChange={(e) => setClientId(e.target.value)} />
        <input style={s.input} placeholder="site id" value={siteId} onChange={(e) => setSiteId(e.target.value)} />
        <input style={s.input} placeholder="device id (optional)" value={deviceId} onChange={(e) => setDeviceId(e.target.value)} />
        <input style={s.input} placeholder="hostname (optional)" value={hostname} onChange={(e) => setHostname(e.target.value)} />
      </div>
      <button style={s.primary} disabled={busy} onClick={submit}>Save ids and reconcile</button>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#080B12', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', color: '#C7CFDC' },
  container: { maxWidth: 1100, margin: '0 auto', padding: '32px 28px 90px' },
  center: { textAlign: 'center', padding: '80px 0', color: '#9b9486' },
  headerRow: { display: 'flex', alignItems: 'center', gap: 14 },
  h1: { fontSize: 30, fontWeight: 900, color: '#fff', margin: 0 },
  badge: { background: 'linear-gradient(135deg,#CA8A0C,#F2B91E)', color: '#1a1206', fontWeight: 800, fontSize: 12, borderRadius: 20, padding: '5px 12px' },
  sub: { fontSize: 14.5, color: '#9fb0c4', lineHeight: 1.6, margin: '10px 0 20px', maxWidth: 820 },

  reqCard: { background: '#111722', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: '16px 18px', marginBottom: 22 },
  reqHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' },
  reqTitle: { fontSize: 15, fontWeight: 800, color: '#fff' },
  reqToggle: { fontSize: 12.5, color: '#93c5fd', fontWeight: 700 },
  reqStatusRow: { display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 12 },
  statusItem: { display: 'inline-flex', alignItems: 'center', gap: 7 },
  dot: { width: 9, height: 9, borderRadius: '50%' },
  statusText: { fontSize: 12.5, color: '#c7cfdc' },
  reqBody: { marginTop: 14, borderTop: '1px solid rgba(255,255,255,0.07)', paddingTop: 14 },
  reqBlock: { marginBottom: 14 },
  reqBlockTitle: { fontSize: 13.5, fontWeight: 800, color: '#e8e6e0', marginBottom: 4 },
  reqModel: { fontSize: 12.5, color: '#9fb0c4', lineHeight: 1.6 },
  reqNeeds: { margin: '8px 0 0', paddingLeft: 18 },
  reqNeed: { fontSize: 12.5, color: '#c7cfdc', marginBottom: 3 },
  reqFoot: { fontSize: 12, color: '#7c8aa0', marginTop: 6 },

  filters: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14, margin: '4px 0 16px' },
  filterGroup: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  filterLabel: { fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#7c8aa0' },
  pill: { background: '#0e1420', border: '1px solid rgba(255,255,255,0.14)', color: '#c7cfdc', borderRadius: 999, padding: '6px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' },
  pillOn: { borderColor: '#F2B91E', background: 'rgba(242,185,30,0.16)', color: '#fff' },
  refreshBtn: { marginLeft: 'auto', background: 'transparent', color: '#93c5fd', border: '1px solid rgba(147,197,253,0.4)', borderRadius: 8, padding: '7px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' },

  empty: { padding: '40px 0', textAlign: 'center', color: '#7c8aa0', fontSize: 14 },
  taskList: { display: 'flex', flexDirection: 'column', gap: 12 },
  taskCard: { background: '#111722', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 16 },
  taskTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  taskType: { fontSize: 15, fontWeight: 800, color: '#fff' },
  provTag: { fontSize: 11, fontWeight: 800, color: '#a7f3d0', background: 'rgba(16,185,129,0.14)', borderRadius: 6, padding: '2px 8px', marginLeft: 8 },
  svcTag: { fontSize: 11, fontWeight: 700, color: '#bfdbfe', background: 'rgba(37,99,235,0.16)', borderRadius: 6, padding: '2px 8px', marginLeft: 6 },
  statusTag: { fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' },
  taskMeta: { display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8, fontSize: 12, color: '#9fb0c4' },
  code: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11.5, color: '#c7cfdc' },
  taskDetail: { fontSize: 13, color: '#c7cfdc', marginTop: 8, lineHeight: 1.5, background: 'rgba(255,255,255,0.03)', borderRadius: 8, padding: '8px 10px', wordBreak: 'break-word' },
  taskDate: { fontSize: 11.5, color: '#6b7688', marginTop: 6 },
  taskActions: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 },
  primary: { background: 'linear-gradient(135deg,#CA8A0C,#F2B91E)', color: '#1a1206', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 12.5, fontWeight: 800, cursor: 'pointer' },
  ghost: { background: 'transparent', color: '#C7CFDC', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8, padding: '8px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' },
  ghostQuiet: { background: 'transparent', color: '#7c8aa0', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '8px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' },

  mapForm: { marginTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 12 },
  mapNote: { fontSize: 12, color: '#9fb0c4', lineHeight: 1.5, marginBottom: 10 },
  mapGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 10 },
  input: { background: '#0b1018', border: '1px solid rgba(255,255,255,0.16)', borderRadius: 8, padding: '9px 11px', color: '#fff', fontSize: 13, boxSizing: 'border-box' },

  overlay: { position: 'fixed', inset: 0, background: 'rgba(4,6,10,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: 20 },
  modal: { background: '#0f141d', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14, padding: '22px 24px', width: 'min(680px, 96vw)', maxHeight: '86vh', overflowY: 'auto' },
  modalTitle: { fontSize: 18, fontWeight: 900, color: '#fff', margin: '0 0 8px' },
  modalNote: { fontSize: 12.5, color: '#9fb0c4', lineHeight: 1.5, margin: '0 0 12px' },
  pre: { background: '#0b0f16', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, padding: 12, fontSize: 12, color: '#c7cfdc', overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', marginTop: 14 },

  err: { fontSize: 13.5, color: '#fca5a5', margin: '8px 0' },
  toast: { position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: '#12131b', border: '1px solid rgba(242,185,30,0.5)', color: '#fff', borderRadius: 10, padding: '11px 18px', fontSize: 13.5, fontWeight: 600, zIndex: 300 },
};
